import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';
import {
  getCampYear,
  getCurrentYearPlaceCode,
  isTrackedAccommodationPlaceCode,
  updateCurrentYearAccommodationHistory
} from '../../../utils/accommodationHistoryUtils';

interface ReleaseReservationCardProps {
  registration: Registration;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
  onSendInvoiceEmail: (invoiceDate: string) => Promise<void>;
}

export interface ReleaseReservationCardRef {
  resetStep: () => void;
}

const VALID_ATTENDING_AGE_CATEGORIES = new Set(['Adult', 'AdultStudent', 'YouthChildren']);

const ReleaseReservationCard = forwardRef<ReleaseReservationCardRef, ReleaseReservationCardProps>(({
  registration,
  onRegistrationUpdate,
  onSendInvoiceEmail,
}, ref) => {
  const [stepComplete, setStepComplete] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step3Complete, setStep3Complete] = useState(false);
  const [skipInvoiceEmail, setSkipInvoiceEmail] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!registration) return;

    if (registration.status === 'Assigned') {
      setStepComplete(true);
      setStep2Complete(true);
      setStep3Complete(!!registration?.financial?.invoiceDate);
    } else {
      setStepComplete(false);
      setStep2Complete(false);
      setStep3Complete(false);
    }
  }, [registration]);

  useImperativeHandle(ref, () => ({
    resetStep: () => {
      setStepComplete(false);
      setStep2Complete(false);
      setStep3Complete(false);
    },
  }));

  const handleReleaseReservation = async () => {
    if (processing) return;

    const invalidAttendingCampers = (registration?.attendingCampers || []).filter((camper: any) => {
      const camperAge = camper?.age ?? camper?.category;
      return !VALID_ATTENDING_AGE_CATEGORIES.has(String(camperAge || ''));
    });
    if (invalidAttendingCampers.length > 0) {
      const invalidCamperList = invalidAttendingCampers
        .map((camper: any) => `${camper?.firstName || ''} ${camper?.lastName || ''}`.trim() || 'Unnamed camper')
        .join(', ');
      alert(`Release blocked. Please fix attending camper age category for: ${invalidCamperList}. Valid categories are Adult, AdultStudent, or YouthChildren.`);
      return;
    }

    setStepComplete(false);
    setStep2Complete(false);
    setStep3Complete(false);
    setProcessing(true);

    try {
      const assignedAccommodation = registration?.financial?.accommodationFees?.assignedAccommodation;
      const primaryCamperId = registration?.registrationInfo?.primaryCamperId;

      if (primaryCamperId && assignedAccommodation) {
        const campYear = await getCampYear();
        const historyPlaceCode = await getCurrentYearPlaceCode(primaryCamperId, campYear);

        if (
          isTrackedAccommodationPlaceCode(historyPlaceCode) &&
          historyPlaceCode!.toLowerCase() !== assignedAccommodation.toLowerCase()
        ) {
          const shouldUpdate = window.confirm(
            `Assigned unit is ${assignedAccommodation}, but stay history shows ${historyPlaceCode}. Update stay history to ${assignedAccommodation}?`
          );

          if (shouldUpdate) {
            await updateCurrentYearAccommodationHistory(
              primaryCamperId,
              assignedAccommodation,
              campYear
            );
          }
        }
      }

      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        status: 'Assigned'
      });

      setStepComplete(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const invoiceDate = `${day}-${month}-${year}`;

      await updateDoc(registrationRef, {
        'financial.invoiceDate': invoiceDate
      });

      setStep2Complete(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const recipientEmail = registration?.registrationInfo?.email || registration?.email;
      if (!skipInvoiceEmail && recipientEmail) {
        try {
          await onSendInvoiceEmail(invoiceDate);
          console.log('Release invoice email queued successfully');
          setStep3Complete(true);
        } catch (emailError) {
          console.error('Failed to send invoice email:', emailError);
        }
      }

      onRegistrationUpdate({
        ...registration,
        status: 'Assigned',
        financial: {
          ...registration.financial,
          invoiceDate: invoiceDate
        }
      });
    } catch (error) {
      console.error('Error releasing reservation:', error);
      alert('Failed to release reservation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const canRelease = registration?.status === 'Hold';

  return (
    <div className="card mb-3" style={{ borderColor: '#007bff', borderWidth: '2px' }}>
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#28a745', fontSize: '1.1rem' }}>
          Release Reservation
        </h6>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: stepComplete ? '#28a745' : '#fff',
              border: '2px solid ' + (stepComplete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {stepComplete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: '#333' }}>
              Change status from Hold to Assigned
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: step2Complete ? '#28a745' : '#fff',
              border: '2px solid ' + (step2Complete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {step2Complete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: '#333' }}>
              Set invoice date
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: step3Complete ? '#28a745' : '#fff',
              border: '2px solid ' + (step3Complete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {step3Complete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: skipInvoiceEmail ? '#999' : '#333', textDecoration: skipInvoiceEmail ? 'line-through' : 'none' }}>
              Send invoice email with PDF attachment
              {skipInvoiceEmail && (
                <span style={{ textDecoration: 'none', color: '#999', fontStyle: 'italic', marginLeft: '0.5rem' }}>
                  (skipped)
                </span>
              )}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#333', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={skipInvoiceEmail}
              onChange={(e) => setSkipInvoiceEmail(e.target.checked)}
              disabled={processing}
            />
            Skip sending invoice email
          </label>
        </div>

        <button
          onClick={handleReleaseReservation}
          disabled={!canRelease || processing}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: canRelease && !processing ? '#dc3545' : '#dee2e6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: canRelease && !processing ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          {processing ? 'Processing...' : 'Release Reservation'}
        </button>

        {!canRelease && registration?.status === 'Assigned' && (
          <p style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.85rem', color: '#666' }}>
            This reservation has already been released.
          </p>
        )}
      </div>
    </div>
  );
});

ReleaseReservationCard.displayName = 'ReleaseReservationCard';

export default ReleaseReservationCard;
